import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import type { ReactNode } from 'react';
import type { LooseObjectType } from 'utils/globalTypes';
import { useAppSelector } from 'app/hooks';
import { logErrors } from 'utils';
import { getFromBrandConfig } from 'brand-config';
import {
  selectBrandCode,
  selectIsBrandDetailsExist,
} from 'services/brand/brandSelectors';
// import { selectAccessToken } from 'services/session/sessionSelectors';
import { selectOfferPlanCode } from 'services/offer/offerSelectors';

declare const window: {
  adaSettings: LooseObjectType;
  adaEmbed: LooseObjectType;
} & typeof global;

type WindowInfo = {
  hasActiveChatter: boolean;
  hasClosedChat: boolean;
  isChatOpen: boolean;
  isDrawerOpen: boolean;
};

type AdaChatProviderProps = {
  children: ReactNode;
};

const AdaChatContext = createContext({
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  openAdaChat(campaignName: string, payload?: LooseObjectType) {},
  isAdaChatAvailable: false,
});

function AdaChatProvider({ children }: AdaChatProviderProps) {
  const brandCode = useAppSelector(selectBrandCode);
  const planCode = useAppSelector(selectOfferPlanCode);
  // const userAccessToken = useAppSelector(selectAccessToken);
  const isBrandDetailsExist = useAppSelector(selectIsBrandDetailsExist);

  /* Not providing ADA Chat feature for eyeon users */
  const isAdaChatAvailable = useMemo(() => {
    if (!isBrandDetailsExist) {
      return false;
    }

    return getFromBrandConfig('adaChat');
  }, [brandCode, isBrandDetailsExist]);

  const showAdaChat = useCallback((action: boolean) => {
    window?.adaEmbed.getInfo().then((windowInfo: WindowInfo) => {
      if (windowInfo.isDrawerOpen !== action) {
        window?.adaEmbed.toggle();
      }
    });
  }, []);

  useEffect(() => {
    let script = document.getElementById('__ada') as HTMLScriptElement;

    if (isAdaChatAvailable && !script) {
      window.adaSettings = {
        adaReadyCallback: () => {
          showAdaChat(false);
        },
      };

      script = document.createElement('script');
      script.src = 'https://static.ada.support/embed2.js';
      script.id = '__ada';
      script.setAttribute('data-handle', 'pango-gr');
      script.onerror = () => {
        logErrors('Error loading script');
      };

      document.head.appendChild(script);
    }

    // return () => {
    //   if (script) {
    //     document.head.removeChild(script);
    //   }
    // };
  }, [isAdaChatAvailable]);

  const openAdaChat = useCallback(
    (campaignName: string, payload?: LooseObjectType) => {
      if (isAdaChatAvailable) {
        if (window?.adaEmbed) {
          showAdaChat(true);
          window?.adaEmbed.setMetaFields({
            brand: brandCode,
            planCode,
            pageName: window.location.pathname,
            ...(payload ?? {}),
          });

          window?.adaEmbed.triggerCampaign(campaignName, {
            ignoreStatus: false,
            ignoreFrequency: false,
          });

          // window?.adaEmbed.setSensitiveMetaFields({
          //   Ada_JWT_Token_Captured: userAccessToken,
          // });
        } else {
          logErrors('Ada script is not loaded or AdaEmbed is not available');
        }
      }
    },
    [planCode, isAdaChatAvailable, brandCode],
  );

  const value = useMemo(
    () => ({
      openAdaChat,
      isAdaChatAvailable,
    }),
    [isAdaChatAvailable, openAdaChat],
  );

  return (
    <AdaChatContext.Provider value={value}>{children}</AdaChatContext.Provider>
  );
}

export const useAdaChat = () => useContext(AdaChatContext);

export default AdaChatProvider;
